import type { ReactNode } from 'react'
import { AlertTriangle, Zap, Thermometer, BatteryWarning } from 'lucide-react'
import { useAppStore } from '@/store/useAppStore.ts'
import { getChemistryPreset } from '@brt/shared-logic'
import { getTemperatureFactor } from '@brt/shared-logic'
import { validateCRate } from '@brt/shared-logic'
import { convertLoad } from '@brt/shared-logic'
import { LoadUnit, type BatteryChemistry } from '@brt/shared-types'

interface WarningItem {
  id: string
  severity: 'caution' | 'danger'
  icon: ReactNode
  text: string
  recommendation: string
}

function getWarnings(
  chemistry: BatteryChemistry,
  dod: number,
  tempC: number,
  soh: number,
  loadA: number,
  capacityAh: number,
): WarningItem[] {
  const warnings: WarningItem[] = []
  const preset = getChemistryPreset(chemistry)

  if (dod > preset.maxDoD) {
    warnings.push({
      id: 'dod-exceeds',
      severity: 'caution',
      icon: <AlertTriangle size={16} />,
      text: `DoD ${(dod * 100).toFixed(0)}% exceeds safe maximum ${(preset.maxDoD * 100).toFixed(0)}%`,
      recommendation: `Reduce DoD to ${(preset.maxDoD * 100).toFixed(0)}% or below`,
    })
  }

  const cRateResult = validateCRate(loadA, capacityAh, chemistry)
  if (cRateResult.status === 'danger') {
    warnings.push({
      id: 'crate-danger',
      severity: 'danger',
      icon: <Zap size={16} />,
      text: `C-rate ${cRateResult.cRate.toFixed(2)}C exceeds peak limit (${preset.peakCRate}C)`,
      recommendation: `Reduce load to ${(preset.peakCRate * capacityAh).toFixed(1)}A or below`,
    })
  } else if (cRateResult.status === 'caution') {
    warnings.push({
      id: 'crate-caution',
      severity: 'caution',
      icon: <AlertTriangle size={16} />,
      text: `C-rate ${cRateResult.cRate.toFixed(2)}C exceeds safe limit (${preset.maxSafeCRate}C)`,
      recommendation: `Reduce load to ${(preset.maxSafeCRate * capacityAh).toFixed(1)}A for optimal safety`,
    })
  }

  if (tempC < preset.minTempC || tempC > preset.maxTempC) {
    warnings.push({
      id: 'temp-range',
      severity: 'caution',
      icon: <Thermometer size={16} />,
      text: `Temperature ${tempC}°C outside safe range (${preset.minTempC}°C to ${preset.maxTempC}°C)`,
      recommendation: `Operate between ${preset.minTempC}°C and ${preset.maxTempC}°C`,
    })
  }

  const tempFactor = getTemperatureFactor(tempC, chemistry)
  if (tempFactor < 0.7 && tempC >= preset.minTempC && tempC <= preset.maxTempC) {
    warnings.push({
      id: 'temp-derating',
      severity: 'caution',
      icon: <Thermometer size={16} />,
      text: `Temperature derating at ${(tempFactor * 100).toFixed(0)}% — significant capacity loss`,
      recommendation: `Move battery to a warmer location if possible`,
    })
  }

  if (soh < 0.70) {
    warnings.push({
      id: 'soh-low',
      severity: 'danger',
      icon: <BatteryWarning size={16} />,
      text: `SoH at ${(soh * 100).toFixed(0)}% — battery critically degraded`,
      recommendation: 'Replace battery for reliable performance',
    })
  }

  return warnings
}

export default function BoundaryWarnings() {
  const battery = useAppStore((s) => s.battery)
  const loads = useAppStore((s) => s.load.loads)

  const totalLoadA = loads
    .filter((l) => l.enabled)
    .reduce((sum, l) => {
      try {
        return sum + convertLoad(l.loadValue, l.loadUnit, LoadUnit.A, battery.voltage)
      } catch {
        return sum
      }
    }, 0)

  const warnings = getWarnings(
    battery.chemistry,
    battery.dod,
    battery.temperatureC,
    battery.stateOfHealth,
    totalLoadA,
    battery.capacityValue,
  )

  if (warnings.length === 0) return null

  return (
    <div className="boundary-warnings" role="alert" aria-live="polite">
      {warnings.map((w) => (
        <div key={w.id} className={`boundary-warnings__item boundary-warnings__item--${w.severity}`}>
          <span className="boundary-warnings__icon" aria-hidden="true">{w.icon}</span>
          <div className="boundary-warnings__content">
            <p className="boundary-warnings__text">{w.text}</p>
            <p className="boundary-warnings__recommendation">{w.recommendation}</p>
          </div>
        </div>
      ))}
    </div>
  )
}
